import { useState } from "react";
import { Edit2, Bell, MessageSquare, Mail } from "lucide-react";
import { SettingsShell, useSettings, SaveBar } from "./SettingsShell";

/**
 * Notifications → Customer sub-tab.
 * All SMS toggles disabled with "Coming soon" chip (no Twilio integration yet).
 * Invoice → Sent + Invoice → Reminder are already LIVE; the rest save to
 * settings.notifications and will fire once wired in a future item.
 */
const EVENT_SECTIONS = [
  {
    id: "jobs",
    title: "Jobs",
    events: [
      { key: "job_scheduled",    label: "Scheduled",  description: "Sent to the customer when a job is scheduled.",                                    live: false },
      { key: "on_my_way",        label: "On my way",  description: "Sent when a technician marks 'On my way' from the mobile app.",                    live: false },
      { key: "finished",         label: "Finished",   description: "Sent when a job is marked complete.",                                              live: false },
      { key: "job_reminder",     label: "Reminder",   description: "Appointment reminder — configurable schedule in Notifications defaults.",          live: false },
      { key: "cancelled",        label: "Cancelled",  description: "Sent if a scheduled job is cancelled.",                                            live: false },
    ],
  },
  {
    id: "estimates",
    title: "Estimates",
    events: [
      { key: "estimate_sent",      label: "Sent",       description: "Sent when an estimate is delivered to the customer.",                             live: true },
      { key: "estimate_reminder",  label: "Reminder",   description: "Follow-up when an estimate remains un-approved.",                                 live: false },
      { key: "estimate_approved",  label: "Approved",   description: "Sent when the customer approves an estimate from the portal.",                    live: false },
      { key: "estimate_declined",  label: "Declined",   description: "Sent when the customer declines an estimate.",                                    live: false },
    ],
  },
  {
    id: "invoices",
    title: "Invoices",
    events: [
      { key: "invoice_sent",     label: "Sent",     description: "Sent when an invoice is emailed to the customer.",                                 live: true },
      { key: "invoice_reminder", label: "Reminder", description: "Automatic follow-up on unpaid invoices — schedule in Settings → Invoice reminders.", live: true },
      { key: "invoice_refund",   label: "Refund",   description: "Sent when a payment is refunded.",                                                  live: false },
      { key: "invoice_receipt",  label: "Receipt",  description: "Sent when a payment is received.",                                                  live: false },
    ],
  },
];

const DEFAULT_TEMPLATES = {
  job_scheduled:      "Hi {customer_first_name}, your appointment with {company_name} is scheduled for {service_date}.",
  on_my_way:          "Hi {customer_first_name}, {technician_name} from {company_name} is on the way.",
  finished:           "Hi {customer_first_name}, your job with {company_name} is complete. Thank you for choosing us!",
  job_reminder:       "Reminder: your appointment with {company_name} is scheduled for {service_date}.",
  cancelled:          "Hi {customer_first_name}, your appointment with {company_name} on {service_date} has been cancelled.",
  estimate_sent:      "Hi {customer_first_name}, please review estimate #{estimate_number} from {company_name}.",
  estimate_reminder:  "Hi {customer_first_name}, estimate #{estimate_number} from {company_name} is still awaiting your approval.",
  estimate_approved:  "Thanks {customer_first_name}, we've received your approval on estimate #{estimate_number}.",
  estimate_declined:  "Hi {customer_first_name}, we're sorry estimate #{estimate_number} wasn't the right fit. Let us know if we can help.",
  invoice_sent:       "Hi {customer_first_name}, your invoice #{invoice_number} from {company_name} is ready to view.",
  invoice_reminder:   "Reminder: invoice #{invoice_number} from {company_name} is still unpaid.",
  invoice_refund:     "Hi {customer_first_name}, your refund on invoice #{invoice_number} has been processed.",
  invoice_receipt:    "Thank you, {customer_first_name}! We received your payment on invoice #{invoice_number}.",
};

export default function SettingsNotifications() {
  const { settings, patch, save, loading, saving, saved, err } = useSettings();
  const [activeSubTab, setActiveSubTab] = useState("customer");
  const [editing, setEditing] = useState(null); // event key
  if (loading || !settings) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const notifs = settings.notifications || {};
  const getEvent = (key) => notifs[key] || { email: false, template: DEFAULT_TEMPLATES[key] || "" };
  const updateEvent = (key, partial) => {
    patch({ notifications: { ...notifs, [key]: { ...getEvent(key), ...partial } } });
  };

  return (
    <SettingsShell
      title="Notifications"
      subtitle="Automated messages sent to your customers."
      testid="settings-notifications"
      subTabs={[{ key: "customer", label: "Customer" }]}
      activeSubTab={activeSubTab}
      onSubTabChange={setActiveSubTab}
    >
      <div className="space-y-6">
        {EVENT_SECTIONS.map((section) => (
          <section key={section.id} data-testid={`notif-section-${section.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <header className="px-6 py-3 border-b border-slate-200 flex items-center gap-2">
              <Bell size={14} className="text-slate-400" />
              <h2 className="font-display font-semibold text-slate-900">{section.title}</h2>
            </header>
            <div>
              {section.events.map((ev) => {
                const evData = getEvent(ev.key);
                return (
                  <div key={ev.key} data-testid={`notif-event-${ev.key}`} className="px-6 py-4 border-b border-slate-100 last:border-b-0">
                    <div className="flex items-start justify-between gap-6">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{ev.label}</span>
                          {ev.live ? (
                            <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">Live</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Setting saved · trigger not yet wired</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{ev.description}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <ChannelToggle
                          icon={Mail}
                          label="Email"
                          checked={!!evData.email}
                          onChange={(v) => updateEvent(ev.key, { email: v })}
                          testid={`notif-email-${ev.key}`}
                        />
                        <ChannelToggle
                          icon={MessageSquare}
                          label="SMS"
                          checked={false}
                          onChange={() => {}}
                          disabled
                          comingSoon
                          testid={`notif-sms-${ev.key}`}
                        />
                        <button
                          type="button"
                          onClick={() => setEditing(editing === ev.key ? null : ev.key)}
                          data-testid={`notif-edit-${ev.key}`}
                          className="p-1.5 text-slate-500 hover:text-[#0F4C81] hover:bg-slate-50 rounded"
                          title="Edit template"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    </div>

                    {editing === ev.key && (
                      <div className="mt-3 pl-0">
                        <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Message template</label>
                        <textarea
                          data-testid={`notif-template-${ev.key}`}
                          rows={3}
                          value={evData.template || DEFAULT_TEMPLATES[ev.key] || ""}
                          onChange={(e) => updateEvent(ev.key, { template: e.target.value })}
                          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] font-mono"
                          placeholder={DEFAULT_TEMPLATES[ev.key]}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                          Placeholders: <code>{"{customer_first_name}"}</code>, <code>{"{company_name}"}</code>, <code>{"{invoice_number}"}</code>, <code>{"{estimate_number}"}</code>, <code>{"{service_date}"}</code>, <code>{"{technician_name}"}</code>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} err={err} testid="settings-notifications" />
    </SettingsShell>
  );
}

function ChannelToggle({ icon: Icon, label, checked, onChange, disabled, comingSoon, testid }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testid}
        title={comingSoon ? "SMS coming soon" : ""}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
          disabled ? "bg-slate-200 cursor-not-allowed" : checked ? "bg-[#0F4C81]" : "bg-slate-300 hover:bg-slate-400"
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </button>
      <span className={`text-[10px] flex items-center gap-0.5 ${disabled ? "text-slate-400" : "text-slate-600"}`}>
        <Icon size={10} /> {label}
      </span>
    </div>
  );
}
